import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { motion } from "framer-motion";
import { Mail, Lock, ArrowRight, Eye, EyeOff } from "lucide-react";
import { api } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import SEO from "@/components/SEO";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      return toast.error("Email and password are required");
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", {
        email,
        password,
      });
      if (!data.token || !data.user) {
        console.error("Invalid login response:", data);
        toast.error("Invalid response from server");
        return;
      }
      login(data.token, data.user);
      toast.success("Welcome to BillForge");
      nav("/app");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Login"
        description="Sign in to your Bill Forge account to manage your invoices, customers, and business."
        path="/login"
        noindex={true}
      />
      <div className="min-h-screen flex items-center justify-center p-6 sm:p-12 bg-white dark:bg-[#0A0A0B] text-slate-900 dark:text-slate-100">
        <Toaster position="top-right" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="uppercase text-xs tracking-[0.3em] text-slate-500 mb-3">Access</div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tighter mb-2">
            Sign in.
          </h1>
          <p className="text-slate-500 mb-10">Enter your credentials to access your account.</p>

          <form onSubmit={handleLogin} className="space-y-5">
            <label className="block">
              <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Email</span>
              <div className="mt-2 flex items-center gap-3 border border-slate-300 dark:border-slate-700 rounded-sm px-4 py-3 focus-within:border-slate-900 dark:focus-within:border-white transition-colors">
                <Mail className="w-4 h-4 text-slate-500" />
                <input
                  data-testid="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-transparent outline-none text-lg"
                  required
                />
              </div>
            </label>

            <label className="block">
              <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Password</span>
              <div className="mt-2 flex items-center gap-3 border border-slate-300 dark:border-slate-700 rounded-sm px-4 py-3 focus-within:border-slate-900 dark:focus-within:border-white transition-colors">
                <Lock className="w-4 h-4 text-slate-500" />
                <input
                  data-testid="password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-transparent outline-none text-lg"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="mt-2 flex justify-between items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 dark:border-slate-700 text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-slate-500">Remember me</span>
                </label>
                <button
                  type="button"
                  onClick={() => nav("/forgot-password")}
                  className="text-sm text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors"
                >
                  Forgot password?
                </button>
              </div>
            </label>

            <button
              data-testid="login-button"
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 py-4 rounded-sm font-semibold hover:bg-[#002FA7] hover:text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? "Signing in..." : "Sign In"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-500 text-sm">
              Don't have an account?{" "}
              <button
                onClick={() => {
                  const redirectPath = new URLSearchParams(location.search).get('redirect');
                  nav(redirectPath ? `/signup?redirect=${encodeURIComponent(redirectPath)}` : '/signup');
                }}
                className="text-[#002FA7] hover:underline font-medium"
              >
                Create an account
              </button>
            </p>
          </div>
        </motion.div>
      </div>
    </>
  );
}
